import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RouteComponentProps, withRouter, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BeatLoader } from 'react-spinners';
import RestService from '../Services/RestService';
import OKomLoading from '../Components/OsloKommuneUI/OKomLoading';
import OKomBreadcrumb from '../Components/OsloKommuneUI/OKomBreadcrumb';
import OKomRadioGroup from '../Components/OsloKommuneUI/OKomRadioGroup';
import { useUserContext } from '../Components/StateContext/UserContext';

type Filter = 'all' | 'received' | 'sent';

const POLL_INTERVAL_MS = 60000;

const formatDateTime = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}.${mm}.${d.getFullYear()} ${hh}:${mi}`;
};

const counterparty = (m: Api.MailboxMessage): string => {
  if (m.Direction === 'sent') return m.ToAddresses ?? '';
  return m.FromName ? `${m.FromName} <${m.FromAddress ?? ''}>` : (m.FromAddress ?? '');
};

const tableStyle: React.CSSProperties = { borderCollapse: 'collapse', fontSize: '0.875rem', width: '100%' };
const thStyle: React.CSSProperties = { border: '1px solid #ccc', padding: '6px 8px', background: '#f0f0f0', textAlign: 'left' };
const tdStyle: React.CSSProperties = { border: '1px solid #ccc', padding: '6px 8px', verticalAlign: 'top' };

const FellesEpostView: React.FC<RouteComponentProps> = () => {
  const { userRoles } = useUserContext();
  const isAdmin = userRoles.includes('admin');
  const { account } = useParams<{ account: string }>();

  const [isLoading, setIsLoading] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [messages, setMessages] = useState<Api.MailboxMessage[]>([]);
  const [accountEmail, setAccountEmail] = useState<string>(account);
  const [filter, setFilter] = useState<Filter>('all');
  const [selected, setSelected] = useState<Api.MailboxMessage | null>(null);

  // Compose / reply form state.
  const [composeOpen, setComposeOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [inReplyToMessageId, setInReplyToMessageId] = useState<string | undefined>(undefined);

  const fetchMessages = useCallback(async (showSpinner: boolean) => {
    try {
      if (showSpinner) setIsLoading(true);
      const data = await RestService.get<Api.MailboxMessage[]>(`/api/mailbox/All/${account}`);
      setMessages(data ?? []);
    } catch (e) {
      if (showSpinner) toast.error(`Kunne ikke laste e-post: ${e}`);
    } finally {
      if (showSpinner) setIsLoading(false);
    }
  }, [account]);

  // Resolve the account's display email for the breadcrumb/header.
  useEffect(() => {
    if (!isAdmin) return;
    RestService.get<Api.MailboxAccountInfo[]>('/api/mailbox/Accounts')
      .then(list => {
        const match = (list ?? []).find(a => a.Key === account);
        if (match) setAccountEmail(match.Email);
      })
      .catch(() => { /* fall back to the key */ });
  }, [isAdmin, account]);

  // Initial load + background poll of our DB (Hangfire does the IMAP polling server-side).
  const fetchRef = useRef(fetchMessages);
  fetchRef.current = fetchMessages;
  useEffect(() => {
    if (!isAdmin) return;
    fetchRef.current(true);
    const id = window.setInterval(() => fetchRef.current(false), POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [isAdmin]);

  // Manual "Oppdater" — kick an immediate IMAP poll, then refetch shortly after.
  const triggerPoll = async () => {
    if (isPolling) return;
    setIsPolling(true);
    try {
      await RestService.post<{}, {}>('/api/mailbox/Poll', {});
      toast.info('Henter ny e-post …');
      window.setTimeout(() => fetchMessages(false), 4000);
    } catch (e) {
      toast.error(`Kunne ikke oppdatere: ${e}`);
    } finally {
      window.setTimeout(() => setIsPolling(false), 4000);
    }
  };

  const filtered = useMemo(() => {
    if (filter === 'all') return messages;
    return messages.filter(m => m.Direction === filter);
  }, [messages, filter]);

  const openCompose = () => {
    setInReplyToMessageId(undefined);
    setTo('');
    setCc('');
    setSubject('');
    setBody('');
    setComposeOpen(true);
  };

  const openReply = (m: Api.MailboxMessage) => {
    setInReplyToMessageId(m.MessageId);
    setTo(m.Direction === 'sent' ? (m.ToAddresses ?? '') : (m.FromAddress ?? ''));
    setCc('');
    const subj = m.Subject ?? '';
    setSubject(/^re:/i.test(subj) ? subj : `Re: ${subj}`);
    const quoted = (m.BodyText ?? '').split('\n').map(l => `> ${l}`).join('\n');
    setBody(`\n\n--- ${formatDateTime(m.MessageDate)} ${counterparty(m)} skrev: ---\n${quoted}`);
    setComposeOpen(true);
  };

  const send = async () => {
    if (!to.trim()) { toast.warn('Mottaker mangler.'); return; }
    setIsSending(true);
    try {
      await RestService.post<Api.SendMailRequest, {}>('/api/mailbox/Send', {
        Account: account,
        To: to.trim(),
        Cc: cc.trim() || undefined,
        Subject: subject,
        Body: body,
        InReplyToMessageId: inReplyToMessageId,
      });
      toast.success('E-post sendt.');
      setComposeOpen(false);
      fetchMessages(false);
    } catch (e) {
      toast.error(`Kunne ikke sende: ${e}`);
    } finally {
      setIsSending(false);
    }
  };

  if (!isAdmin) {
    return (
      <>
        <OKomBreadcrumb path='Epost' />
        <div className="ods-grid ods-grid--center ods-margin-top-25">
          <div className="ods-grid__column--8">
            <div className="ods-status-card ods-color-bg-red-light">
              <div className="ods-status-card__content">
                <span className="ods-text--size-kilo">Ingen tilgang. Denne siden er kun for administratorer.</span>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <OKomBreadcrumb path={`Epost, ${accountEmail}`} />
      {isLoading && <OKomLoading text='Henter e-post …' />}

      <div className="ods-margin-top-11 ods-grid ods-grid--gap ods-grid--center" style={{ maxWidth: '95rem' }}>
        <div className="ods-grid__column--12">
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" className="ods-button" onClick={openCompose}>Ny e-post</button>
              <button type="button" className="ods-button ods-button--outline" onClick={triggerPoll} disabled={isPolling}>
                Oppdater
                {isPolling && (
                  <span className="ods-margin-left-1" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                    <BeatLoader color="#2a2859" size={8} />
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="ods-grid__column--12 ods-margin-top-3">
          <OKomRadioGroup
            legend="Vis"
            name="mailfilter"
            inline
            value={filter}
            onChange={(v) => setFilter(v as Filter)}
            options={[
              { value: 'all', label: 'Alle' },
              { value: 'received', label: 'Mottatt' },
              { value: 'sent', label: 'Sendt' },
            ]}
          />
        </div>

        <div className="ods-grid__column--12 ods-margin-top-3" style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Retning</th>
                <th style={thStyle}>Fra / Til</th>
                <th style={thStyle}>Emne</th>
                <th style={thStyle}>Dato</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(m => (
                <tr key={m.Id} style={{ cursor: 'pointer' }} onClick={() => setSelected(m)}>
                  <td style={tdStyle}>{m.Direction === 'sent' ? '↑ Sendt' : '↓ Mottatt'}</td>
                  <td style={tdStyle}>{counterparty(m)}</td>
                  <td style={tdStyle}>{m.Subject}{m.HasAttachments ? ' 📎' : ''}</td>
                  <td style={tdStyle}>{formatDateTime(m.MessageDate)}</td>
                </tr>
              ))}
              {filtered.length === 0 && !isLoading && (
                <tr><td style={tdStyle} colSpan={4}>Ingen e-post.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selected && <MessageDetail message={selected} onClose={() => setSelected(null)} onReply={openReply} />}
      {composeOpen && (
        <ComposeForm
          to={to} setTo={setTo}
          cc={cc} setCc={setCc}
          subject={subject} setSubject={setSubject}
          body={body} setBody={setBody}
          isReply={!!inReplyToMessageId}
          isSending={isSending}
          onSend={send}
          onClose={() => setComposeOpen(false)}
        />
      )}
    </>
  );
};

// --- Message detail (read body). HTML bodies render in a sandboxed iframe (no scripts) to
// avoid XSS from untrusted external email; plain-text bodies render as text. ---
const MessageDetail: React.FC<{
  message: Api.MailboxMessage;
  onClose: () => void;
  onReply: (m: Api.MailboxMessage) => void;
}> = ({ message, onClose, onReply }) => (
  <ModalShell onClose={onClose}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '1rem' }}>
      <h3 style={{ margin: 0 }}>{message.Subject}</h3>
      <button type="button" className="ods-button ods-button--small" onClick={() => { onReply(message); onClose(); }}>Svar</button>
    </div>
    <div className="ods-margin-top-2 ods-text--size-kilo">
      <div><strong>{message.Direction === 'sent' ? 'Til:' : 'Fra:'}</strong> {counterparty(message)}</div>
      {message.Direction === 'sent' && message.FromAddress && <div><strong>Fra:</strong> {message.FromAddress}</div>}
      {message.Direction === 'received' && message.ToAddresses && <div><strong>Til:</strong> {message.ToAddresses}</div>}
      {message.CcAddresses && <div><strong>Kopi:</strong> {message.CcAddresses}</div>}
      <div><strong>Dato:</strong> {formatDateTime(message.MessageDate)}</div>
    </div>
    <hr className="ods-margin-top-2" />
    <div className="ods-margin-top-2">
      {message.BodyText
        ? <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>{message.BodyText}</pre>
        : message.BodyHtml
          ? <iframe title="E-post" sandbox="" srcDoc={message.BodyHtml} style={{ width: '100%', minHeight: '50vh', border: 'none' }} />
          : <em>Ingen innhold.</em>}
    </div>
  </ModalShell>
);

const ComposeForm: React.FC<{
  to: string; setTo: (v: string) => void;
  cc: string; setCc: (v: string) => void;
  subject: string; setSubject: (v: string) => void;
  body: string; setBody: (v: string) => void;
  isReply: boolean;
  isSending: boolean;
  onSend: () => void;
  onClose: () => void;
}> = ({ to, setTo, cc, setCc, subject, setSubject, body, setBody, isReply, isSending, onSend, onClose }) => (
  <ModalShell onClose={onClose}>
    <h3 style={{ marginTop: 0 }}>{isReply ? 'Svar' : 'Ny e-post'}</h3>
    <label className="ods-input__label">Til
      <input className="ods-input__input" type="text" value={to} onChange={e => setTo(e.target.value)} placeholder="mottaker@eksempel.no" />
    </label>
    <label className="ods-input__label ods-margin-top-2">Kopi (valgfritt)
      <input className="ods-input__input" type="text" value={cc} onChange={e => setCc(e.target.value)} />
    </label>
    <label className="ods-input__label ods-margin-top-2">Emne
      <input className="ods-input__input" type="text" value={subject} onChange={e => setSubject(e.target.value)} />
    </label>
    <label className="ods-input__label ods-margin-top-2">Melding
      <textarea className="ods-input__input" rows={12} value={body} onChange={e => setBody(e.target.value)} style={{ resize: 'vertical' }} />
    </label>
    <div className="ods-margin-top-3" style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
      <button type="button" className="ods-button ods-button--outline" onClick={onClose} disabled={isSending}>Avbryt</button>
      <button type="button" className="ods-button" onClick={onSend} disabled={isSending}>
        Send
        {isSending && (
          <span className="ods-margin-left-1" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
            <BeatLoader color="#ffffff" size={8} />
          </span>
        )}
      </button>
    </div>
  </ModalShell>
);

const ModalShell: React.FC<{ onClose: () => void; children: React.ReactNode }> = ({ onClose, children }) => (
  <div
    onClick={onClose}
    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '3rem 1rem', overflowY: 'auto', zIndex: 1000 }}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{ background: '#fff', borderRadius: 4, padding: '1.5rem', width: '100%', maxWidth: '48rem', boxShadow: '0 4px 24px rgba(0,0,0,0.2)' }}
    >
      {children}
    </div>
  </div>
);

export default withRouter(FellesEpostView);
